<template>
  <el-scrollbar>
    <el-menu default-active="/home" router unique-opened :class="{ fold: props.fold }">
      <template v-for="menu in filterMenusByAuth()" :key="menu.index">
        <template v-if="menu.children">
          <el-sub-menu :index="menu.index">
            <template #title>
              <el-icon :title="menu.title">
                <component :is="menu.icon" />
              </el-icon>

              <span class="item-title">{{ menu.title }}</span>
            </template>

            <el-menu-item v-for="child in menu.children" :key="child.index" :index="child.index">
              <el-icon :title="child.title">
                <component :is="child.icon" />
              </el-icon>

              <span class="item-title">{{ child.title }}</span>
            </el-menu-item>
          </el-sub-menu>
        </template>

        <template v-else>
          <el-menu-item :index="menu.index">
            <el-icon :title="menu.title">
              <component :is="menu.icon" />
            </el-icon>

            <el-badge v-if="menu.isTodo && !fold" :value="todoCountStore.count" color="red" :show-zero="false" :offset="[7, 12]">
              <span class="item-title">{{ menu.title }}</span>
            </el-badge>

            <span v-else class="item-title">{{ menu.title }}</span>
          </el-menu-item>
        </template>
      </template>
    </el-menu>
  </el-scrollbar>
</template>

<script setup>
import { useTodoCountStore } from '@/store/todoCount'
import { useAuthStore } from '@/store/auth'

const props = defineProps({
  fold: {
    type: Boolean,
    default: false
  }
})

const menus = [
  {
    title: '首页',
    icon: 'House',
    index: '/home',
    auth: true
  },
  {
    title: '待办事项',
    icon: 'Document',
    index: '/todoList',
    auth: true,
    isTodo: true
  },
  {
    title: '系统功能',
    icon: 'Grid',
    index: '/system',
    children: [
      {
        title: '角色管理',
        icon: 'UserFilled',
        index: '/system/role'
      },
      {
        title: '用户管理',
        icon: 'User',
        index: '/system/user'
      },
      {
        title: '组织架构',
        icon: 'School',
        index: '/system/architecture'
      },
      {
        title: '数据字典',
        icon: 'Document',
        index: '/system/dictionary'
      },
      {
        title: '执行周期',
        icon: 'Clock',
        index: '/system/period'
      },
      {
        title: '操作日志',
        icon: 'Document',
        index: '/system/operation'
      },
      {
        title: '其他设置',
        icon: 'Setting',
        index: '/system/setting'
      }
    ]
  },
  {
    title: '风险分级管控',
    icon: 'Warning',
    index: '/core',
    children: [
      {
        title: '风险四色图',
        icon: 'Picture',
        index: '/core/map',
        moduleCode: 'core.map'
      },
      {
        title: '风险管理',
        icon: 'Compass',
        index: '/core/risk/risk',
        moduleCode: 'core.risk',
        auth: ['core/risk/risk', 'core/risk/device', 'core/risk/job', 'core/risk/std'],
        mix: true
      },
      {
        title: '隐患排查',
        icon: 'Tickets',
        index: '/core/check/checkTask',
        moduleCode: 'core.check',
        auth: ['core/check/checkTask', 'core/check/riskTask', 'core/check/checkList'],
        mix: true
      },
      {
        title: '隐患发现',
        icon: 'Bell',
        index: '/core/hazard',
        moduleCode: 'core.hazard'
      }
    ]
  },
  {
    title: '设备管理',
    icon: 'Box',
    index: '/specialEquip',
    children: [
      {
        title: '特种设备',
        icon: 'Box',
        index: '/specialEquip/equipment',
        moduleCode: 'special-equip.equipment'
      },
      {
        title: '安全附件',
        icon: 'Link',
        index: '/specialEquip/accessory',
        moduleCode: 'special-equip.accessory'
      }
    ]
  },
  {
    title: '特种设备',
    icon: 'Link',
    index: '/specialEquip_0001',
    children: [
      {
        title: '月调度',
        icon: 'Calendar',
        index: '/specialEquip_0001/schedule',
        moduleCode: 'special-equip_0001.schedule'
      },
      {
        title: '特种设备',
        icon: 'Link',
        index: '/specialEquip_0001/equipment',
        moduleCode: 'special-equip_0001.equipment'
      },
      {
        title: '安全阀',
        icon: 'Link',
        index: '/specialEquip_0001/valve',
        moduleCode: 'special-equip_0001.valve'
      },
      {
        title: '压力表',
        icon: 'Link',
        index: '/specialEquip_0001/pressure',
        moduleCode: 'special-equip_0001.pressure'
      },
      {
        title: '爆破片',
        icon: 'Link',
        index: '/specialEquip_0001/burst',
        moduleCode: 'special-equip_0001.burst'
      }
    ]
  },
  {
    title: '培训考试',
    icon: 'Notebook',
    index: '/trainExam',
    children: [
      {
        title: '线上培训',
        icon: 'Reading',
        index: '/trainExam/onlineTrain/regular',
        moduleCode: 'train-exam.online',
        auth: ['trainExam/onlineTrain/regular', 'trainExam/onlineTrain/risk', 'trainExam/onlineTrain/resource'],
        mix: true
      },
      {
        title: '线下培训',
        icon: 'Reading',
        index: '/trainExam/offlineTrain/train',
        moduleCode: 'train-exam.offline',
        auth: ['trainExam/offlineTrain/train', 'trainExam/offlineTrain/eval'],
        mix: true
      },
      {
        title: '考试管理',
        icon: 'EditPen',
        index: '/trainExam/exam/exam',
        moduleCode: 'train-exam.exam',
        auth: ['trainExam/exam/exam', 'trainExam/exam/paper', 'trainExam/exam/question'],
        mix: true
      }
    ]
  },
  {
    title: '票据办理',
    icon: 'Tickets',
    index: '/ticket',
    children: [
      {
        title: '作业票',
        icon: 'Tickets',
        index: '/ticket/job/regular',
        moduleCode: 'ticket.job',
        auth: ['ticket/job/regular', 'ticket/job/special'],
        mix: true
      },
      {
        title: '其他票据',
        icon: 'Tickets',
        index: '/ticket/regular',
        moduleCode: 'ticket.regular'
      },
      {
        title: '票据模板',
        icon: 'Tickets',
        index: '/ticket/template',
        moduleCode: 'ticket.template'
      }
    ]
  },
  {
    title: '行为管理',
    icon: 'Van',
    index: '/behavior',
    children: [
      {
        title: '违规记录',
        icon: 'CircleClose',
        index: '/behavior/violation',
        moduleCode: 'behavior.violation'
      },
      {
        title: '违规计分',
        icon: 'Coin',
        index: '/behavior/violationScore',
        moduleCode: 'behavior.violation-score'
      }
    ]
  },
  {
    title: '积分管理',
    icon: 'Coin',
    index: '/score',
    children: [
      {
        title: '安全积分',
        icon: 'Van',
        index: '/score/safety',
        moduleCode: 'score.safety'
      },
      {
        title: '积分商品',
        icon: 'Present',
        index: '/score/goods',
        moduleCode: 'score.goods'
      }
    ]
  },
  {
    title: '统计分析',
    icon: 'DataAnalysis',
    index: '/statistics',
    moduleCode: 'statistics'
  }
]

const todoCountStore = useTodoCountStore()
const authStore = useAuthStore()

function filterMenusByAuth() {
  const list = []
  for (const group of menus) {
    if (!hasAuth(group)) continue
    const { ...copy } = group
    if (copy.children) {
      copy.children = copy.children.filter((it) => hasAuth(it))
      if (copy.children.length === 0) continue
      for (const child of copy.children) {
        if (child.mix) {
          //此时需要调整index值，因为默认值可能已经不适用(无权限)
          child.index = '/' + child.auth.filter((it) => authStore.has(it))[0]
        }
      }
    }
    list.push(copy)
  }
  return list
}

function hasAuth(menuItem) {
  const { auth, index } = menuItem
  return (
    auth === true ||
    //未定义auth属性时取值index（即路由uri）
    (auth === undefined && authStore.has(index.substring(1))) ||
    //auth为字符串时
    (typeof auth === 'string' && authStore.has(auth)) ||
    //auth为字符串数组时，任意一项通过则通过
    (Array.isArray(auth) && authStore.hasAny(auth))
  )
}
</script>

<style lang="scss" scoped>
.el-menu {
  border-right: 0;
  --el-menu-active-color: var(--my-bg-color-secondary);
  --el-menu-text-color: var(--my-text-color);
  --el-menu-hover-text-color: var(--my-text-color);
  --el-menu-bg-color: var(--my-bg-color);
  --el-menu-hover-bg-color: var(--my-bg-color-secondary);
}
.el-menu.fold {
  display: flex;
  flex-direction: column;
  align-items: center;
  .el-menu-item {
    padding: 0;
  }
  .el-icon {
    margin-right: auto;
    margin-left: auto;
  }
  .item-title {
    display: none;
  }
  &:deep(.el-sub-menu__title) {
    padding: 0;
  }
  &:deep(.el-sub-menu__icon-arrow) {
    display: none;
  }
}
.el-menu-item.is-active {
  background-color: var(--my-bg-color-secondary);
  color: var(--my-text-color);
}
.el-scrollbar {
  flex: 1;
  width: 100%;
}
.item-title {
  user-select: none;
}
</style>
